import React, { useState, useCallback, useMemo, useContext } from 'react';
import { ChevronLeft, Loader2, Mail, AlertCircle } from 'lucide-react';
import Bellicon from '../../utils/bellicon';
import { toast } from 'react-toastify';
import { AppContext } from '../../apollo/wrap-root-element';

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERROR_MESSAGES = {
  EMPTY: 'Please enter an email address.',
  INVALID: 'Please enter a valid email address.',
  NETWORK: 'Network error. Please check your connection and try again.',
  SERVER: 'Failed to set reminder. Please try again.',
};

const API_CONFIG = {
  endpoint: `${process.env.GATSBY_LINEAGE_BACKEND_URL_DEV}/donationintrest`,
  headers: {
    'Content-Type': 'application/json',
  },
};

export default function EmailReminderModal({ setReminder, onClose }) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setShowPopup } = useContext(AppContext);

  // Memoized validation functions
  const isValidEmail = useMemo(() => {
    const trimmedEmail = email.trim();
    return trimmedEmail && EMAIL_REGEX.test(trimmedEmail);
  }, [email]);

  const isFormValid = useMemo(() => {
    return isValidEmail && !emailError;
  }, [isValidEmail, emailError]);

  // Validation helpers
  const validateEmail = useCallback((emailValue) => {
    const trimmedEmail = emailValue.trim();

    if (!trimmedEmail) {
      return ERROR_MESSAGES.EMPTY;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      return ERROR_MESSAGES.INVALID;
    }

    return '';
  }, []);

  // Event handlers
  const handleEmailChange = useCallback(
    (e) => {
      const value = e.target.value;
      setEmail(value);
      if (emailError) {
        setEmailError('');
      }
    },
    [emailError]
  );

  const handleEmailBlur = useCallback(() => {
    const error = validateEmail(email);
    setEmailError(error);
  }, [email, validateEmail]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    setReminder(false);
    setShowPopup(false);
  }, [isLoading, setReminder]);

  // API call 
  const submitEmailReminder = useCallback(
    async (emailAddress) => {
      setIsLoading(true);

      try {
        const response = await fetch(API_CONFIG.endpoint, {
          method: 'POST',
          headers: API_CONFIG.headers,
          body: JSON.stringify({ email: emailAddress }),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          // Use API error message if available
          const errorMessage = data?.message || `HTTP ${response.status}: ${response.statusText}`;
          toast.error(errorMessage);
          return;
        }

        if (data.success) {
          toast.success('Reminder set successfully! We will notify you via email.');
          handleClose();
        } else {
          const errorMessage = data?.message || ERROR_MESSAGES.SERVER;
          toast.error(errorMessage);
          console.error('API Error:', data);
        }
      } catch (error) {
        console.error('Network Error:', error);
        toast.error(ERROR_MESSAGES.NETWORK);
      } finally {
        setIsLoading(false);
      }
    },
    [handleClose]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const error = validateEmail(email);
      if (error) {
        setEmailError(error);
        return;
      }

      await submitEmailReminder(email.trim());
    },
    [email, validateEmail, submitEmailReminder]
  );

  // Keyboard event handler for accessibility
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && !isLoading) {
        handleClose();
      }
    },
    [isLoading, handleClose]
  );

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="email-reminder-modal" role="dialog" aria-labelledby="modal-title" aria-modal="true">
        <div className="modal-header">
          <button
            className="back-button"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
            aria-label="Close modal"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 id="modal-title" className="modal-title">
            Maybe Next Time?
          </h2>
        </div>

        <div className="modal-content">
          <div className="icon-container" aria-hidden="true">
            <Bellicon />
          </div>

          <p className="description">
            Please leave your email address below, and we'll send you a gentle reminder later.
          </p>

          <form onSubmit={handleSubmit} className="email-form" noValidate>
            <div className="form-group">
              <label htmlFor="email" className="email-label">
                Email address*
              </label>

              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  className={`email-input ${emailError ? 'error' : ''} ${isValidEmail ? 'valid' : ''}`}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                  required
                  autoComplete="email"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'email-error' : undefined}
                />
              </div>

              {emailError && (
                <div style={{
                  color: "red",
                  marginTop: "0.5rem",
                }} id="email-error" className="error-message" role="alert" aria-live="polite">
                  <AlertCircle size={14} className="error-icon" aria-hidden="true" />
                  {emailError}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="remind-button"
              disabled={isLoading || !isFormValid}
              aria-describedby="submit-button-description"
            >
              {isLoading ? (
                <>
                  Setting Reminder...
                </>
              ) : (
                <>Remind Me Later</>
              )}
            </button>
          </form>

          <button className="no-thanks-button" onClick={handleClose} disabled={isLoading} type="button">
            No, thanks
          </button>
        </div>
      </div>
    </div>
  );
}
